"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useDebounce } from "@/hooks/utility/useDebounce";
import Image from "next/image";
import { Typography } from "@/components/shared/typography/typography";
import { cn } from "@/lib/utils";
import MarketplaceNavbar from "./Navbar";
import { MarketplaceMobileHeader } from "./MarketplaceMobileHeader";
import { FilterBarProvider, FilterBarTags } from "./listing/Filter";
import { FiltersModal } from "./listing/FiltersModal";
import { ListingGrid } from "./listing/ListingGrid";
import { ListingGridSkeleton } from "./listing/ListingGridSkeleton";
import { ListingShowMoreButton } from "./listing/ListingShowMoreButton";
import Beliefs from "@/components/features/home/beliefs/Beliefs";
import { searchMarketplaceProducts } from "@/api/services/marketplace";
import {
  buildMarketplaceListingUrl,
  buildMarketplaceListingUrlWithPlace,
  buildProductSearchRequest,
  clearFilterTypesInParams,
  ensureFilterMappingIncludesUrlTerms,
  MARKETPLACE_KEYWORDS_FILTER_TYPE,
  mapProductSearchResponse,
  marketplaceKeys,
  parseCheckboxFiltersFromSearchParams,
  parseMarketplaceSearchFromParams,
  setCheckboxFilterInParams,
} from "@/lib/marketplace";
import { useMarketplaceListing } from "@/hooks/queries/useMarketplaceListing";
import { useMarketplaceSearchSuggestionsData } from "@/hooks/queries/useMarketplaceSearchSuggestionsData";
import { getMarketplaceModalFilters } from "@/lib/marketplace";
import { scrollToMarketplaceResults } from "./listing/scrollToMarketplaceResults";
import { useQueryClient } from "@tanstack/react-query";
import type {
  MarketplaceListingData,
  MarketplaceListingItem,
  MarketplaceFilterValue,
  MarketplaceSearchResult,
  MarketplaceSelectedFilters,
} from "@/types/api/marketplace";
import type { ProductPagination } from "@/types/api/marketplace-product";

interface MarketplaceListingProps {
  initialData: MarketplaceListingData;
  filterMapping: Record<string, Record<number, string>>;
  initialPagination: ProductPagination;
  initialSearchSuggestions: MarketplaceSearchResult[];
}

function countModalFilters(
  filters: MarketplaceSelectedFilters,
  modalTypes: Set<string>
): number {
  return Object.entries(filters).reduce((total, [key, value]) => {
    if (!modalTypes.has(key)) return total;
    if (Array.isArray(value)) return total + value.length;
    if (value && typeof value === "object") {
      const obj = value as Record<string, string | undefined>;
      return total + Object.values(obj).filter(Boolean).length;
    }
    return total;
  }, 0);
}

const resultsSummaryClass =
  "pt-4 text-sm leading-snug text-gray-900 md:pt-6 md:text-xl md:leading-normal";

function ResultsSummary({ summary }: { summary: string }) {
  const colonIndex = summary.indexOf(":");

  if (colonIndex === -1) {
    return (
      <Typography variant="p" className={cn(resultsSummaryClass, "font-bold")}>
        {summary}
      </Typography>
    );
  }

  return (
    <Typography variant="p" className={resultsSummaryClass}>
      <span className="font-bold">{summary.slice(0, colonIndex + 1)}</span>
      <span className="font-normal">{summary.slice(colonIndex + 1)}</span>
    </Typography>
  );
}

function applyLocalFilterChange(
  prev: MarketplaceSelectedFilters,
  type: string,
  value: MarketplaceFilterValue | undefined
): MarketplaceSelectedFilters {
  const next = { ...prev };
  if (value === undefined) {
    delete next[type];
  } else {
    next[type] = value;
  }
  return next;
}

export default function MarketplaceListing({
  initialData,
  filterMapping: initialFilterMapping,
  initialPagination,
  initialSearchSuggestions,
}: MarketplaceListingProps) {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const router = useRouter();
  const searchKey = searchParams.toString();
  const appliedSearch = parseMarketplaceSearchFromParams(searchParams);

  const [localFilters, setLocalFilters] = useState<MarketplaceSelectedFilters>(
    {}
  );
  const [searchDraft, setSearchDraft] = useState<string | null>(null);
  const searchInput = searchDraft ?? appliedSearch;
  const debouncedSearchInput = useDebounce(searchInput, 300);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filterMapping, setFilterMapping] = useState(initialFilterMapping);
  const [accumulatedListings, setAccumulatedListings] = useState<
    MarketplaceListingItem[]
  >([]);

  const resolvedFilterMapping = ensureFilterMappingIncludesUrlTerms(
    filterMapping,
    searchParams
  );
  const checkboxFilters = parseCheckboxFiltersFromSearchParams(
    searchParams,
    resolvedFilterMapping
  );
  const selectedFilters: MarketplaceSelectedFilters = {
    ...checkboxFilters,
    ...localFilters,
  };

  const { data, isLoading, isFetching } = useMarketplaceListing(
    searchKey,
    resolvedFilterMapping,
    page
  );

  const { data: suggestionData, isFetching: isSuggestionsFetching } =
    useMarketplaceSearchSuggestionsData(debouncedSearchInput);

  const listingData = data?.listingData ?? initialData;
  const currentPagination = data?.pagination ?? initialPagination;
  const currentListings = listingData.listings;
  const searchSuggestions = suggestionData ?? initialSearchSuggestions;

  const isApplyingFilters = page === 1 && (isFetching || isLoading);

  const stableFiltersRef = useRef(listingData.dynamic_filters);
  const stableSummaryRef = useRef(listingData.results_summary);

  if (!isApplyingFilters) {
    stableFiltersRef.current = listingData.dynamic_filters;
    stableSummaryRef.current = listingData.results_summary;
  }

  const filterBarFilters = isApplyingFilters
    ? stableFiltersRef.current
    : listingData.dynamic_filters;
  const resultsSummary = isApplyingFilters
    ? stableSummaryRef.current
    : listingData.results_summary;

  const displayedListings =
    page === 1
      ? currentListings
      : [...accumulatedListings, ...currentListings];

  const hasMoreListings =
    currentPagination.page * currentPagination.page_size <
    currentPagination.total_count;

  const modalFilterTypes = new Set(
    getMarketplaceModalFilters(filterBarFilters).map((f) => f.type)
  );

  const activeModalFilterCount = countModalFilters(
    selectedFilters,
    modalFilterTypes
  );

  const pushUrl = (url: string) => {
    router.replace(url, { scroll: false });
  };

  const handleFilterChange = (
    type: string,
    value: MarketplaceFilterValue | undefined
  ) => {
    if (Array.isArray(value) || value === undefined) {
      pushUrl(
        setCheckboxFilterInParams(
          searchParams,
          type,
          value,
          appliedSearch,
          resolvedFilterMapping
        )
      );
    } else {
      setLocalFilters((prev) => applyLocalFilterChange(prev, type, value));
    }
  };

  const handleFiltersModalOpenChange = (open: boolean) => {
    if (filtersModalOpen && !open) {
      scrollToMarketplaceResults();
    }
    setFiltersModalOpen(open);
  };

  const clearModalFilters = () => {
    pushUrl(
      clearFilterTypesInParams(
        searchParams,
        modalFilterTypes,
        appliedSearch,
        resolvedFilterMapping
      )
    );
    setLocalFilters((prev) => {
      const next = { ...prev };
      modalFilterTypes.forEach((type) => delete next[type]);
      return next;
    });
  };

  useEffect(() => {
    setPage(1);
    setAccumulatedListings([]);
  }, [searchKey]);

  useEffect(() => {
    if (data?.filterMapping && Object.keys(data.filterMapping).length > 0) {
      setFilterMapping((prev) => ({ ...prev, ...data.filterMapping }));
    }
  }, [data?.filterMapping]);

  const handleSearchInputChange = (value: string) => {
    setSearchDraft(value);
  };

  const handleSearchCommit = (query: string) => {
    const trimmed = query.trim();
    setSearchDraft(null);
    const filters = parseCheckboxFiltersFromSearchParams(
      searchParams,
      resolvedFilterMapping
    );
    delete filters.City;
    delete filters.Country;
    if (trimmed) {
      delete filters[MARKETPLACE_KEYWORDS_FILTER_TYPE];
    }
    pushUrl(
      buildMarketplaceListingUrl(filters, resolvedFilterMapping, trimmed)
    );
    scrollToMarketplaceResults();
  };

  const handleSearchSuggestionSelect = (result: MarketplaceSearchResult) => {
    if (result.href) return;

    if (result.type === "activity") {
      const filters = parseCheckboxFiltersFromSearchParams(
        searchParams,
        resolvedFilterMapping
      );
      delete filters.City;
      delete filters.Country;
      delete filters[MARKETPLACE_KEYWORDS_FILTER_TYPE];
      setSearchDraft(null);
      pushUrl(
        buildMarketplaceListingUrl(
          filters,
          resolvedFilterMapping,
          result.title
        )
      );
      scrollToMarketplaceResults();
      return;
    }

    if (!result.countryCode) return;

    const filters = parseCheckboxFiltersFromSearchParams(
      searchParams,
      resolvedFilterMapping
    );

    setSearchDraft(result.subtitle || result.title);
    pushUrl(
      buildMarketplaceListingUrlWithPlace({
        checkboxFilters: filters,
        filterMapping: resolvedFilterMapping,
        countryCode: result.countryCode,
        cityName: result.cityName,
        searchText: "",
      })
    );
    scrollToMarketplaceResults();
  };

  const handleLoadMoreListings = async () => {
    if (!hasMoreListings || isLoadingMore) return;

    setIsLoadingMore(true);

    try {
      const nextPage = page + 1;
      const response = await queryClient.fetchQuery({
        queryKey: marketplaceKeys.listing(searchKey, nextPage),
        queryFn: () =>
          searchMarketplaceProducts(
            buildProductSearchRequest(
              checkboxFilters,
              resolvedFilterMapping,
              appliedSearch
            ),
            nextPage
          ),
      });
      const mapped = mapProductSearchResponse(response, appliedSearch);

      if (mapped.listingData.listings.length > 0) {
        setAccumulatedListings((prev) => [...prev, ...currentListings]);
        setPage(nextPage);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const filterBarProps = {
    filters: filterBarFilters,
    selectedFilters,
    onFilterChange: handleFilterChange,
    onOpenFiltersModal: () => setFiltersModalOpen(true),
    activeModalFilterCount,
    isFetchingFilters: isApplyingFilters,
  };

  const navbarProps = {
    searchSuggestions,
    searchValue: searchInput,
    onSearchChange: handleSearchInputChange,
    onSearchCommit: handleSearchCommit,
    onSearchSuggestionSelect: handleSearchSuggestionSelect,
    debouncedSearch: debouncedSearchInput,
    isSearchFetching:
      (isFetching && Boolean(appliedSearch.trim())) || isSuggestionsFetching,
  };

  return (
    <FilterBarProvider {...filterBarProps}>
      <div className="min-h-screen bg-white">
        <MarketplaceMobileHeader {...navbarProps}>
          <FilterBarTags slot="mobile" />
        </MarketplaceMobileHeader>

        <div className="sticky top-0 z-50 hidden bg-white shadow-sm md:block">
          <MarketplaceNavbar {...navbarProps} />
          <FilterBarTags slot="desktop" />
        </div>

        <div className="bg-white">
          <FiltersModal
            open={filtersModalOpen}
            onOpenChange={handleFiltersModalOpenChange}
            filters={filterBarFilters}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAll={clearModalFilters}
          />

          <main className="bg-white pb-10 md:pb-8">
            <div
              id="marketplace-listings"
              className="app-container scroll-mt-28 space-y-6 pb-10 md:scroll-mt-52 md:space-y-8 md:pb-0">
              {isApplyingFilters ? (
                <div className="h-6 w-32 animate-pulse rounded bg-gray-200 md:h-7 md:w-40" />
              ) : (
                <ResultsSummary summary={resultsSummary} />
              )}

              {isApplyingFilters ? (
                <ListingGridSkeleton count={8} />
              ) : displayedListings.length > 0 ? (
                <div className="space-y-6">
                  <ListingGrid items={displayedListings} />
                  {hasMoreListings && (
                    <ListingShowMoreButton
                      onClick={handleLoadMoreListings}
                      isLoading={isLoadingMore}
                    />
                  )}
                </div>
              ) : (
                <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
                  <Image
                    src="/icons/no-packages.svg"
                    alt="No results"
                    width={96}
                    height={96}
                    className="mb-4"
                  />
                  <Typography
                    variant="p-lg"
                    className="mb-2 font-bold text-gray-900">
                    No activities found
                  </Typography>
                  <Typography variant="p-sm" className="text-gray-500">
                    Try adjusting your filters or search term.
                  </Typography>
                </div>
              )}
            </div>
          </main>

          <Beliefs />
        </div>
      </div>
    </FilterBarProvider>
  );
}
